import { useTranslation } from '@/lib/i18n';
import { FilterBar, FilterField, Input, RadixSelect } from '@/components/ui';
import type {
  SettlementCycleEnum,
  SettlementRunStatusEnum,
  SettlementRunTypeEnum,
} from '@/lib/api-admin/types';
import { useSettlementFiltersStore } from '@/stores/useSettlementFiltersStore';
import { VendorSelector } from '@/features/vendor/components/VendorSelector';

const STATUS_OPTIONS: ReadonlyArray<SettlementRunStatusEnum> = [
  'COMPUTING',
  'COMPUTED',
  'PENDING_APPROVAL',
  'AGENT_APPROVED',
  'SUPER_APPROVED',
  'PAYOUT_INITIATED',
  'PAID_OUT',
  'PAYOUT_FAILED',
  'PAYOUT_RETRY',
  'MANUAL_INTERVENTION_REQUIRED',
  'CANCELED',
  'FAILED',
];

const CYCLE_OPTIONS: ReadonlyArray<SettlementCycleEnum> = ['MONTHLY', 'WEEKLY'];
const RUN_TYPE_OPTIONS: ReadonlyArray<SettlementRunTypeEnum> = ['AUTO', 'MANUAL'];

export const SettlementFiltersBar = () => {
  const { t } = useTranslation('settlement');
  const vendorId = useSettlementFiltersStore((s) => s.vendorId);
  const cycle = useSettlementFiltersStore((s) => s.cycle);
  const runType = useSettlementFiltersStore((s) => s.runType);
  const status = useSettlementFiltersStore((s) => s.status);
  const periodFrom = useSettlementFiltersStore((s) => s.periodFrom);
  const periodTo = useSettlementFiltersStore((s) => s.periodTo);
  const setVendorId = useSettlementFiltersStore((s) => s.setVendorId);
  const setCycle = useSettlementFiltersStore((s) => s.setCycle);
  const setRunType = useSettlementFiltersStore((s) => s.setRunType);
  const setStatuses = useSettlementFiltersStore((s) => s.setStatuses);
  const setPeriodFrom = useSettlementFiltersStore((s) => s.setPeriodFrom);
  const setPeriodTo = useSettlementFiltersStore((s) => s.setPeriodTo);

  const singleStatus: '' | SettlementRunStatusEnum =
    status.length === 1 ? (status[0] as SettlementRunStatusEnum) : '';

  const statusOptions = [
    { value: '', label: t('settlement.filters.statusAll') },
    ...STATUS_OPTIONS.map((opt) => ({ value: opt, label: t(`settlement.status.${opt}`) })),
  ];
  const cycleOptions = [
    { value: '', label: t('settlement.filters.cycleAll') },
    ...CYCLE_OPTIONS.map((opt) => ({ value: opt, label: t(`settlement.cycle.${opt}`) })),
  ];
  const runTypeOptions = [
    { value: '', label: t('settlement.filters.runTypeAll') },
    ...RUN_TYPE_OPTIONS.map((opt) => ({ value: opt, label: t(`settlement.runType.${opt}`) })),
  ];

  return (
    <FilterBar
      aria-label={t('settlement.filters.label')}
      data-testid="settlement-filters"
      filters={
        <>
          <FilterField label={t('settlement.filters.vendorId')}>
            <VendorSelector
              value={vendorId ?? null}
              onChange={setVendorId}
              placeholder={t('settlement.filters.vendorIdPlaceholder')}
            />
          </FilterField>
          <FilterField label={t('settlement.filters.status')}>
            <RadixSelect
              id="settlement-status"
              value={singleStatus}
              onChange={(v) => setStatuses(v === '' ? [] : [v as SettlementRunStatusEnum])}
              options={statusOptions}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('settlement.filters.cycle')}>
            <RadixSelect
              id="settlement-cycle"
              value={cycle ?? ''}
              onChange={(v) => setCycle(v === '' ? null : (v as SettlementCycleEnum))}
              options={cycleOptions}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('settlement.filters.runType')}>
            <RadixSelect
              id="settlement-runtype"
              value={runType ?? ''}
              onChange={(v) => setRunType(v === '' ? null : (v as SettlementRunTypeEnum))}
              options={runTypeOptions}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('settlement.filters.periodFrom')}>
            <Input
              id="settlement-from"
              type="date"
              value={periodFrom ?? ''}
              onChange={(e) => setPeriodFrom(e.target.value === '' ? null : e.target.value)}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('settlement.filters.periodTo')}>
            <Input
              id="settlement-to"
              type="date"
              value={periodTo ?? ''}
              onChange={(e) => setPeriodTo(e.target.value === '' ? null : e.target.value)}
              className="w-full"
            />
          </FilterField>
        </>
      }
    />
  );
};
